import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Check } from "lucide-react";
import { authAPI } from "../../../services/apiService";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Showcase } from "../../components/auth/Showcase";
import { FormError, PasswordInput } from "../../components/auth/fields";
import { AUTH_PATHS } from "../../components/auth/signupState";
import { Field } from "../../components/crm/leadsKit";
import { PageLoader } from "../../components/PageLoader";
import { PrimaryButton } from "../../components/ui";

/**
 * Reset password (no design screen; built like ForgotPassword) on the same calls
 * as the old pages/ResetPassword.js:
 *   GET  /api/auth/verify-reset-token/:token  -> { success, email }   (400 = invalid or expired)
 *   POST /api/auth/reset-password { token, password } -> { success, message }
 * Rules as the old page and authController.resetPassword: at least 8 characters,
 * confirmation must match. On success it sends the person to sign in after 2.5s.
 * A staff invite is this same link, so setting the password also accepts the invite.
 */
export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // checking | valid | invalid | error (the check itself failed: 500 / network)
  const [state, setState] = useState(token ? "checking" : "invalid");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const verify = useCallback(() => {
    if (!token) return;
    setState("checking");
    authAPI
      .verifyResetToken(token)
      .then((r) => {
        if (r.data.success) {
          setEmail(r.data.email);
          setState("valid");
        } else setState("invalid");
      })
      .catch((err) => setState(err.response && err.response.status < 500 ? "invalid" : "error"));
  }, [token]);

  useEffect(() => {
    verify();
  }, [verify]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => navigate(AUTH_PATHS.signin), 2500);
    return () => clearTimeout(t);
  }, [success, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (password.length < 8) next.password = "Password must be at least 8 characters";
    else if (password !== confirm) next.confirm = "Passwords do not match";
    setErrors(next);
    setFormError("");
    if (Object.keys(next).length) return;
    setLoading(true);
    try {
      const r = await authAPI.resetPassword(token, password);
      if (r.data.success) setSuccess(true);
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const backLink = (
    <Link to={AUTH_PATHS.signin} className="flex items-center gap-2 self-start text-small font-medium text-muted hover:text-ink">
      <ArrowLeft size={16} strokeWidth={1.75} />
      Back to sign in
    </Link>
  );

  let body;
  if (state === "checking") {
    body = <PageLoader label="Verifying reset link…" className="min-h-[240px]" />;
  } else if (state === "invalid" || state === "error") {
    const failed = state === "error";
    body = (
      <div key={state} className="flex animate-fade-in flex-col gap-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-danger-soft text-danger">
          <AlertCircle size={24} strokeWidth={1.75} />
        </span>
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 font-semibold">{failed ? "Could not check this link" : "Link expired or invalid"}</h1>
          <p className="text-body text-muted">
            {failed
              ? "Something went wrong while checking your reset link. Try again in a moment."
              : "This password reset link has expired or has already been used. Please request a new one."}
          </p>
        </div>
        {failed ? (
          <PrimaryButton type="button" onClick={verify} className="self-start">Try again</PrimaryButton>
        ) : (
          <Link
            to={AUTH_PATHS.forgot}
            className="flex h-12 items-center justify-center self-start rounded-full bg-sun px-6 text-body font-semibold text-ink hover:brightness-95"
          >
            Request new link
          </Link>
        )}
      </div>
    );
  } else if (success) {
    body = (
      <div key="done" className="flex animate-fade-in flex-col gap-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-sun">
          <Check size={24} strokeWidth={1.75} />
        </span>
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 font-semibold">Password reset</h1>
          <p className="text-body text-muted">Your password has been updated successfully.</p>
          <p className="text-small text-muted">Redirecting you to sign in…</p>
        </div>
      </div>
    );
  } else {
    body = (
      <form key="form" onSubmit={submit} noValidate className="flex animate-fade-in flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 font-semibold">Choose a new password</h1>
          <p className="break-words text-body text-muted">
            for <span className="font-medium text-ink">{email}</span>
          </p>
        </div>
        {formError && <FormError>{formError}</FormError>}
        <div className="flex flex-col gap-5">
          <Field id="password" label="New password" hint="At least 8 characters" error={errors.password}>
            <PasswordInput
              id="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              invalid={!!errors.password}
            />
          </Field>
          <Field id="confirm" label="Confirm new password" error={errors.confirm}>
            <PasswordInput
              id="confirm"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
              invalid={!!errors.confirm}
            />
          </Field>
        </div>
        <PrimaryButton type="submit" loading={loading} loadingLabel="Resetting…">
          Reset password
        </PrimaryButton>
      </form>
    );
  }

  return (
    <AuthLayout aside={<Showcase step="forgot" />}>
      <div className="flex flex-col gap-8">
        {backLink}
        {body}
      </div>
    </AuthLayout>
  );
}
